<template>
  <div class="flex flex-col flex-1 p-4 w-full mx-auto">
    <div class="relative">
      <!-- Date range & Refresh -->
      <div
        class="flex flex-col lg:flex-row justify-between lg:justify-end lg:items-center lg:space-x-2 space-y-4 lg:space-y-0 sticky shadow p-4 top-12 z-100 bg-white"
      >
        <div class="flex items-center space-x-6">
          <!-- Refresh -->
          <BaseButtonIcon
            :loading="pendingDataFetching"
            size="sm"
            icon="refresh"
            class="text-primary"
            @click="getReportingDetails(meter)"
          >
          </BaseButtonIcon>
          <div class="relative">
            <BaseCheck
              label="live_update"
              v-tooltip="{content: $t('mygwf.components.User.ReportChart.live_update_tooltip')}"
              :checked="liveUpdate"
              @input="onToggleLiveUpdate"
              class="flex relative"
            />
          </div>
        </div>
        <!-- <div class="absolute z-100 bg-black text-white p-1 -top-8 left-0 text-xs">
          {{ granularity }} - {{ maxRangeText }} - {{ date_after }} - {{ date_before }}
        </div> -->

        <div class="flex xl:justify-end w-full space-x-1">
          <!-- Date range -->
          <BaseButtonIcon action="previous" @click="getPrevious" :loading="pendingDataFetching" />
          <date-picker
            class="flex"
            style="z-index: 1100"
            input-class="bg-secondary-light appearance-none inline-block border rounded-none leading-normal text-base px-2 py-2 h-10 focus:outline-none focus:bg-white focus:border-primary-light pr-10 flex flex-grow w-full max-w-lg"
            :type="'datetime'"
            :format="'MMM DD, YYYY'"
            :editable="false"
            :open.sync="showDatePicker"
            v-model="reportRange"
            range
            confirm
            :confirm-text="$t('mygwf.components.Button.apply')"
            :shortcuts="shortcuts"
            :clearable="false"
            placeholder=""
            :lang="$i18n.locale"
            :disabled-date="disabledBeforePickedRangeAndAfter"
            :show-time-panel="showTimePanel"
            :time-picker-options="{start: '00:00', step: '01:00', end: '23:59', format: 'HH:mm'}"
            @pick="pickDateRange"
          >
            <template v-slot:footer>
              <button class="mx-btn mx-btn-text mr-2" @click="showTimePanel = !showTimePanel">
                {{
                  showTimePanel
                    ? $t('mygwf.components.Label.hide_time_panel')
                    : $t('mygwf.components.Label.show_time_panel')
                }}
              </button>
            </template>
          </date-picker>

          <BaseButtonIcon
            action="next"
            @click="getNext"
            :disabled="disabledBeforePickedRangeAndAfter(reportRange[1])"
            :loading="pendingDataFetching"
          />
          <BaseButtonIcon
            action="zoom_out"
            @click="onReportZoomOut"
            :disabled="granularity === '1Y'"
            :loading="pendingDataFetching"
          />
        </div>
      </div>

      <!-- Chart settings & Export Afm -->
      <div class="flex justify-end mt-4">
        <a-popover
          v-model="showChartSettings"
          trigger="click"
          placement="bottom"
          arrowPointAtCenter
        >
          <template #content>
            <ChartSettings :meter-type="meterType" @close="showChartSettings = false" />
          </template>
          <!-- Activator -->
          <!-- <BaseButtonIcon icon="cog" size="sm" content="primary" class="mr-2" /> -->

          <BaseButton
            class="flex size-sm text-sm mr-2"
            size="sm"
            context="secondary"
            action="report_config"
          >
            <BaseIcon icon="cog" size="sm" content="primary" class="mr-1" />
          </BaseButton>
        </a-popover>
        <base-dropdown-2
          :loading="isDownloading"
          :disabled="isDownloading || pendingDataFetching"
          placement="bottom"
          trigger="click"
          size="sm"
        >
          <template><base-icon icon="download" class="text-xl" /></template>
          <template #body>
            <base-button
              @click="exportAllReports('csv')"
              size="sm"
              context="outline"
              class="w-full border-b"
              action="csv"
            />
            <base-button
              @click="exportAllReports('json')"
              size="sm"
              context="outline"
              class="w-full border-b"
              action="json"
            />
            <base-button
              @click="exportAllReports('xml')"
              size="sm"
              context="outline"
              class="w-full border-b"
              action="xml"
            />
          </template>
        </base-dropdown-2>
      </div>
      <hr class="my-3 border-secondary-light" />

      <!-- Charts -->
      <div>
        <chart
          v-for="chart in chartsToRender"
          :key="chart.id"
          :title="chart.title"
          :unit="chart.unit"
          :dataKey="chart.dataKey"
          :payload="chartsData[chart.payload] ? chartsData[chart.payload] : {}"
          :config="{granOptions, type: chart.type}"
          :min="date_after"
          :max="date_before"
          :includeTime="includeTime"
          :loading="pendingDataFetching"
          @selected="onReportZoomIn"
          @export="exportReports(chart.id, $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BaseCheck,
  BaseSelect,
  BaseLoader,
  BaseIcon,
  BaseButton,
  BaseButtonIcon,
  BaseDropdown2,
} from '@/components/base'
import isEmpty from 'lodash-es/isEmpty'
import fileExport from '@/state/helpers/fileExport.js'
import Chart from './stats/Chart.vue'
import ChartSettings from './ReportChartSettings.vue'
import {
  addYears,
  startOfDay,
  endOfDay,
  addDays,
  addHours,
  addMonths,
  addWeeks,
  min,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInYears,
  differenceInMonths,
  parseISO,
  addMinutes,
} from 'date-fns'
import {endOfHour} from 'date-fns/fp'
import Popover from 'ant-design-vue/lib/popover'
import 'ant-design-vue/lib/popover/style/index.css'

function createEmptyData() {
  return {
    totalVolume: {},
    deltaVolume: {},
    flow: {},
    meanVelocity: {},
    sensorStatus: {},
    spreadFactor: {},
    temperature: {},
    batteryVoltage: {},
    batteryLifetime: {},
    battery1Status: {},
    battery2Status: {},
    level1: {},
  }
}

export default {
  data() {
    return {
      isDownloading: false,
      gran: '1D',
      reportRange: [null, null],
      showDatePicker: false,
      pendingDataFetching: false,
      showTimePanel: false,
      liveUpdate: false,
      liveUpdateInterval: null,
      data: createEmptyData(),
      showChartSettings: false,
      shortcuts: [
        {
          text: this.$t('mygwf.components.User.ReportChart.last_3_hours'),
          onClick: () => {
            this.reportRange = [addHours(new Date(), -3), new Date()]
            this.showDatePicker = false
          },
        },
        {
          text: this.$t('mygwf.components.User.ReportChart.today'),
          onClick: () => {
            this.reportRange = [startOfDay(new Date()), new Date()]
            this.showDatePicker = false
          },
        },
        {
          text: this.$t('mygwf.components.User.ReportChart.yesterday'),
          onClick: () => {
            this.reportRange = [
              startOfDay(addDays(new Date(), -1)),
              endOfDay(addDays(new Date(), -1)),
            ]
            this.showDatePicker = false
          },
        },
        {
          text: this.$t('mygwf.components.User.ReportChart.last_7_days'),
          onClick: () => {
            this.reportRange = [addDays(new Date(), -7), new Date()]
            this.showDatePicker = false
          },
        },
        {
          text: this.$t('mygwf.components.User.ReportChart.last_month'),
          onClick: () => {
            this.reportRange = [addMonths(new Date(), -1), new Date()]
            this.showDatePicker = false
          },
        },
        {
          text: this.$t('mygwf.components.User.ReportChart.last_3_months'),
          onClick: () => {
            this.reportRange = [addMonths(new Date(), -3), new Date()]
            this.showDatePicker = false
          },
        },
      ],
      saveTypeTimer: null,
    }
  },

  computed: {
    includeTime() {
      this.inDay = ['1T', '15T', '1H'].includes(this.granularity)
      return ['1T', '15T', '1H'].includes(this.granularity)
    },
    granularity() {
      const rangeH = differenceInHours(
        Date.parse(this.reportRange[1]),
        Date.parse(this.reportRange[0])
      )
      if (rangeH <= 2) {
        return '1T'
      }
      if (rangeH <= 24) {
        return '15T'
      }

      const rangeD = differenceInDays(
        Date.parse(this.reportRange[1]),
        Date.parse(this.reportRange[0])
      )
      if (rangeD < 3) {
        return '1H'
      }

      const rangeM = differenceInMonths(
        Date.parse(this.reportRange[1]),
        Date.parse(this.reportRange[0])
      )
      if (rangeM < 3) {
        return '1D'
      }

      const rangeY = differenceInYears(
        Date.parse(this.reportRange[1]),
        Date.parse(this.reportRange[0])
      )
      if (rangeY < 3) {
        return '1M'
      }

      return '1Y'
    },
    granOptions() {
      if (['1T', '15T'].includes(this.granularity)) {
        return {unit: 'minute', tooltipFormat: 'kk:mm'}
      }
      if (['1H'].includes(this.granularity)) {
        return {unit: 'hour', tooltipFormat: 'MMM dd kk:mm'}
      }
      if (['1D'].includes(this.granularity)) {
        return {unit: 'day', tooltipFormat: 'MMM dd'}
      }
      if (['1M'].includes(this.granularity)) {
        return {unit: 'month', tooltipFormat: 'MMM yyyy'}
      }
      return {unit: 'year', tooltipFormat: 'yyyy'}
    },
    chartsToRender() {
      const chartsConfig = {
        battery1Status: {
          id: 'battery1Status',
          title: `${this.$t('mygwf.components.User.ReportChart.battery_status')} 1`,
          unit: this.data['battery1Status'].unit,
          payload: 'battery1Status',
          dataKey: 'Battery1Status',
        },
        battery2Status: {
          id: 'battery2Status',
          title: `${this.$t('mygwf.components.User.ReportChart.battery_status')} 2`,
          unit: this.data['battery2Status'].unit,
          payload: 'battery2Status',
          dataKey: 'Battery2Status',
        },
        batteryLifetime: {
          id: 'batteryLifetime',
          title: this.$t('mygwf.components.User.ReportChart.battery_lifetime'),
          unit: this.data['batteryLifetime'].unit,
          payload: 'batteryLifetime',
          dataKey: 'BatteryLifetime',
        },
        batteryVoltage: {
          id: 'batteryVoltage',
          title: this.$t('mygwf.components.User.ReportChart.battery_voltage'),
          unit: this.data['batteryVoltage'].unit,
          payload: 'batteryVoltage',
          dataKey: 'BatteryVoltage',
        },
        deltaVolume: {
          id: 'deltaVolume',
          title: this.$t('mygwf.components.User.ReportChart.delta_volume'),
          unit: this.data['deltaVolume'].unit,
          payload: 'deltaVolume',
          dataKey: 'Delta',
        },
        flow: {
          id: 'flow',
          title: this.$t('mygwf.components.User.ReportChart.flow'),
          unit: this.data['flow'].unit,
          payload: 'flow',
          dataKey: 'Average',
        },
        level1: {
          id: 'level1',
          title: this.$t('mygwf.components.User.ReportChart.level1'),
          unit: this.data['level1'].unit,
          payload: 'level1',
          dataKey: 'Level1',
        },
        meanVelocity: {
          id: 'meanVelocity',
          title: this.$t('mygwf.components.User.ReportChart.mean_velocity'),
          unit: this.data['meanVelocity'].unit,
          payload: 'meanVelocity',
          dataKey: 'Average',
        },
        sensorStatus: {
          id: 'sensorStatus',
          title: this.$t('mygwf.components.User.ReportChart.signal_strength'),
          unit: this.data['sensorStatus'].unit,
          payload: 'sensorStatus',
          dataKey: 'Average',
        },
        spreadFactor: {
          id: 'spreadFactor',
          title: this.$t('mygwf.components.User.ReportChart.spreading_factor'),
          unit: this.data['spreadFactor'].unit,
          payload: 'spreadFactor',
          dataKey: 'SpreadFactor',
        },
        totalVolume: {
          id: 'totalVolume',
          title: this.$t('mygwf.components.User.ReportChart.total_volume'),
          unit: this.data['totalVolume'].unit,
          payload: 'totalVolume',
          dataKey: 'Totals',
        },
      }
      const chartOrder = this.chartSettings.filter(e => e.v)
      let availableCharts = []
      switch (true) {
        case this.isLORA:
          availableCharts = this.volumeReport
            ? ['totalVolume', 'deltaVolume']
            : ['totalVolume', 'deltaVolume', 'spreadFactor', 'batteryLifetime']
          break
        case this.isAFM:
          availableCharts = this.volumeReport
            ? ['totalVolume', 'deltaVolume']
            : [
                'totalVolume',
                'deltaVolume',
                'flow',
                'level1',
                'meanVelocity',
                'sensorStatus',
                'batteryVoltage',
                'battery1Status',
                'battery2Status',
              ]
          break
        default:
          break
      }
      return chartOrder
        .filter(row => availableCharts.includes(row.c))
        .map(row => {
          //Set type of graph
          chartsConfig[row.c].type = row.t
          return chartsConfig[row.c]
        })
    },

    chartsData() {
      const data = this.data
      const cData = createEmptyData()
      let relativeKey

      if (this.includeTime) {
        relativeKey = value => value
      } else if (this.granularity === '1D') {
        relativeKey = value => value.substring(0, 10)
      } else if (this.granularity === '1M') {
        relativeKey = value => value.substring(0, 7)
      } else {
        relativeKey = value => value.substring(0, 4)
      }

      Object.keys(data).forEach(k => {
        if (isEmpty(data[k].data)) {
          return
        }

        if (data[k].data.length === 1) {
          let last = data[k].data[0].records.map(record => {
            const key = Object.keys(record)[0]
            return {x: relativeKey(key), y: record[key]}
          })
          if (data[k].title === 'Total volume' || data[k].title === 'Level 1') {
            cData[k] = [
              {
                label: 'Last value',
                data: last,
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                pointBackgroundColor: 'rgb(54, 162, 235)',
                borderColor: 'rgb(54, 162, 235)',
                fill: true,
                borderWidth: 1,
              },
            ]
          } else if (data[k].title === 'Delta volume') {
            cData[k] = [
              {
                label: 'Last value',
                data: last,
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                pointBackgroundColor: 'rgb(54, 162, 235)',
                borderColor: 'rgb(54, 162, 235)',
                borderWidth: 1,
              },
            ]
          } else {
            cData[k] = [
              {
                label: 'Last value',
                data: last,
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                pointBackgroundColor: 'rgb(75, 192, 192)',
                borderColor: 'rgb(75, 192, 192)',
                borderWidth: 1,
              },
            ]
          }
        } else {
          let max = data[k].data[1].records.map(record => {
            const key = Object.keys(record)[0]
            return {x: relativeKey(key), y: record[key]}
          })
          let min = data[k].data[0].records.map(record => {
            const key = Object.keys(record)[0]
            return {x: relativeKey(key), y: record[key]}
          })
          cData[k] = [
            {
              label: 'Max value',
              data: max,
              backgroundColor: 'rgba(0,69,124, 0.7)',
              borderColor: 'rgba(0,69,124, 0.7)',
            },
            {
              label: 'Min value',
              data: min,
              backgroundColor: 'rgba(251,146,60, 0.7)',
              borderColor: 'rgba(251,146,60, 0.7)',
            },
          ]
        }
      })

      return cData
    },

    isAFM() {
      return this.meter.meter_type === 'AFM'
    },

    isLORA() {
      return !this.isAFM
    },

    noAvailableData() {
      return Object.keys(this.data).every(k => isEmpty(this.data[k]))
    },

    isAfmExportAvailable() {
      return this.isAFM && !isEmpty(this.data.totalVolume)
    },

    granularityOptions() {
      return [
        {id: '1T', label: this.$t('mygwf.components.User.ReportChart.minute')},
        {id: '15T', label: this.$t('mygwf.components.User.ReportChart.quarterly')},
        {id: '1H', label: this.$t('mygwf.components.User.ReportChart.hourly')},
        {id: '1D', label: this.$t('mygwf.components.User.ReportChart.daily')},
        {id: '7D', label: this.$t('mygwf.components.User.ReportChart.weekly')},
        {id: '1M', label: this.$t('mygwf.components.User.ReportChart.monthly')},
        {id: '1Y', label: this.$t('mygwf.components.User.ReportChart.yearly')},
      ]
    },

    maxRangeText() {
      switch (this.granularity) {
        case '1T':
          return this.$t('mygwf.components.Form.max_range_minutes')
        case '15T':
          return this.$t('mygwf.components.Form.max_range_15_minutes')
        case '1H':
          return this.$t('mygwf.components.Form.max_range_hours')
        case '1D':
          return this.$t('mygwf.components.Form.max_range_days')
        case '7D':
          return this.$t('mygwf.components.Form.max_range_week')
        case '1M':
          return this.$t('mygwf.components.Form.max_range_months')
        case '1Y':
          return this.$t('mygwf.components.Form.max_range_years')
        default:
          return ''
      }
    },

    date_after() {
      if (this.includeTime) {
        return this.reportRange[0].toISOString()
      }

      const after = this.reportRange[0]
      after.setUTCHours(0, 0, 0, 0)
      return after.toISOString()
    },

    date_before() {
      if (this.includeTime) {
        return this.reportRange[1].toISOString()
      }

      const before = this.reportRange[1]
      before.setUTCHours(0, 0, 0, 0)
      return before.toISOString()
    },

    meterType() {
      return this.isAFM ? 'afm' : 'rcmlrwotaa'
    },
    chartSettings() {
      return this.$store.getters['reports/charts/getCurrentSettings'](this.meterType)
    },
  },

  beforeDestroy() {
    this.onToggleLiveUpdate(false)
  },

  mounted() {
    this.$root.$on('lastEntryClicked', lastEntry => {
      this.reportRange = [
        addHours(startOfDay(parseISO(lastEntry)), 2),
        addHours(endOfDay(parseISO(lastEntry)), 2),
      ]
    })
  },

  methods: {
    pickDateRange(date) {},

    disabledBeforePickedRangeAndAfter(date) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return date > today
    },

    exportAllReports(type) {
      this.isDownloading = true
      let query = `exportAll`
      const d = new Date()

      let params = {
        serial_number: this.meter.meter_serial,
        date_after: this.date_after,
        date_before: this.date_before,
      }

      if (this.isAfmExportAvailable) {
        query = `exportAfm`
      }

      this.$store
        .dispatch(`reports/${query}`, {params, format: type.toLowerCase()})
        .then(file => {
          const filename = `data_export_${this.meter.meter_serial}-${d
            .toISOString()
            .substring(0, 10)}.${type.toLowerCase()}`
          fileExport(file, filename)
        })
        .then(() => {
          this.$store.dispatch('app/notification', {
            type: 'success',
            group: 'app',
            text: `download`,
          })
        })
        .catch(err => {
          console.log(err)
          this.$store.dispatch('app/notification', {
            type: 'error',
            group: 'app',
            text: `download`,
          })
        })
        .finally(() => (this.isDownloading = false))
    },

    exportReports(stat, type) {
      const d = new Date()
      const filename = `${this.meter.meter_serial}-${stat}-${d
        .toISOString()
        .substring(0, 10)}.${type.toLowerCase()}`

      const dataToExport = this.chartsData[stat]?.[0].data
      switch (type) {
        case 'json':
          const exportStructure = {data: []}

          if (dataToExport.length) {
            exportStructure.data = dataToExport.map(e => {
              return {timestamp: e.x, value: e.y}
            })
          }
          fileExport(JSON.stringify(exportStructure), filename)
          break
        case 'csv':
          let csvContent = 'Timestamp,Value'
          dataToExport.forEach(e => {
            csvContent += `\n${e.x},${e.y}`
          })

          fileExport(csvContent, filename)
          break
      }
    },

    renameObjKey(object, key, newKey) {
      const clonedObj = {...object}
      clonedObj[newKey] = clonedObj[key]

      delete clonedObj[key]
      return clonedObj
    },

    async getReportingDetails(meter, silent = false) {
      if (this.pendingDataFetching) {
        return
      }

      try {
        if (!silent) this.pendingDataFetching = true

        const reportsConfig = {
          battery1Status: 'reports/getBattery1Status',
          battery2Status: 'reports/getBattery2Status',
          batteryLifetime: 'reports/getBatteryLifetime',
          batteryVoltage: 'reports/getBatteryVoltage',
          deltaVolume: 'reports/getDeltaVolume',
          flow: 'reports/getFlow',
          level1: 'reports/getLevel1',
          meanVelocity: 'reports/getMeanVelocity',
          sensorStatus: 'reports/getSensorStatus',
          spreadFactor: 'reports/getSpreadFactor',
          totalVolume: 'reports/getTotalVolume',
        }
        const fetchDataFor = this.chartsToRender.map(c => c.id)

        // Fetch data
        const data = createEmptyData()
        const payload = {
          params: {
            gran: this.granularity,
            datetime_after: this.date_after,
            datetime_before: this.date_before,
          },
          meter_uuid: meter.uuid,
        }
        let requests = []
        let results = []
        fetchDataFor.forEach(e =>
          requests.push(this.$store.dispatch({type: reportsConfig[e], ...payload}))
        )
        results = await Promise.allSettled(requests)
        results.forEach((result, index) => {
          const reportType = fetchDataFor[index]
          data[reportType] = result.status === 'rejected' ? {} : result.value
        })
        this.data = data
      } catch (e) {
        console.log(e)
      } finally {
        this.pendingDataFetching = false
      }
    },

    onToggleLiveUpdate(state) {
      this.liveUpdate = state

      if (this.liveUpdate) {
        this.getReportingDetails(this.meter, true)
        this.liveUpdateInterval = setInterval(
          () => this.getReportingDetails(this.meter, true),
          60000
        )
      } else {
        clearInterval(this.liveUpdateInterval)
        this.liveUpdateInterval = null
      }
    },
    getPrevious() {
      let range

      switch (this.granularity) {
        case '1Y':
          range = differenceInYears(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addYears(this.reportRange[0], -Math.ceil(range / 2)),
            addYears(this.reportRange[1], -Math.ceil(range / 2)),
          ]
          break
        case '1M':
          range = differenceInMonths(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addMonths(this.reportRange[0], -Math.ceil(range / 2)),
            addMonths(this.reportRange[1], -Math.ceil(range / 2)),
          ]
          break
        case '7D':
          range = differenceInDays(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addDays(this.reportRange[0], -range),
            addDays(this.reportRange[1], -range),
          ]
          break
        case '1D':
          range = differenceInDays(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addDays(this.reportRange[0], -range),
            addDays(this.reportRange[1], -range),
          ]
          break
        case '1H':
          range = differenceInHours(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addHours(this.reportRange[0], -range),
            addHours(this.reportRange[1], -range),
          ]
          break
        case '15T':
          range = differenceInMinutes(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addMinutes(this.reportRange[0], -range),
            addMinutes(this.reportRange[1], -range),
          ]
          break
        case '1T':
          range = differenceInMinutes(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addMinutes(this.reportRange[0], -range),
            addMinutes(this.reportRange[1], -range),
          ]
          break
      }
    },
    getNext() {
      let range
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      switch (this.granularity) {
        case '1Y':
          range = differenceInYears(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addYears(this.reportRange[0], Math.ceil(range / 2)),
            addYears(this.reportRange[1], Math.ceil(range / 2)),
          ]
          break
        case '1M':
          range = differenceInMonths(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addMonths(this.reportRange[0], Math.ceil(range / 2)),
            addMonths(this.reportRange[1], Math.ceil(range / 2)),
          ]
          break
        case '7D':
          range = differenceInDays(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addDays(this.reportRange[0], range),
            addDays(this.reportRange[1], range),
          ]
          break
        case '1D':
          range = differenceInDays(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addDays(this.reportRange[0], range),
            addDays(this.reportRange[1], range),
          ]
          break
        case '1H':
          range = differenceInHours(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addHours(this.reportRange[0], range),
            addHours(this.reportRange[1], range),
          ]
          break
        case '15T':
          range = differenceInMinutes(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addMinutes(this.reportRange[0], range),
            addMinutes(this.reportRange[1], range),
          ]
          break
        case '1T':
          range = differenceInMinutes(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addMinutes(this.reportRange[0], range),
            addMinutes(this.reportRange[1], range),
          ]
          break
      }
    },
    onReportZoomOut() {
      let range
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      switch (this.granularity) {
        case '1M':
          range = differenceInMonths(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addMonths(this.reportRange[0], -Math.ceil(range / 2)),
            min([today, addMonths(this.reportRange[1], Math.ceil(range / 2))]),
          ]
          break
        case '7D':
          range = differenceInDays(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addDays(this.reportRange[0], -range),
            min([today, addDays(this.reportRange[1], range)]),
          ]
          break
        case '1D':
          range = differenceInDays(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addDays(this.reportRange[0], -range),
            min([today, addDays(this.reportRange[1], range)]),
          ]
          break
        case '1H':
          range = differenceInHours(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addMonths(this.reportRange[0], -1),
            min([today, addMonths(this.reportRange[1], 1)]),
          ]
          break
        case '15T':
          range = differenceInMinutes(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addMinutes(this.reportRange[0], -range),
            min([today, addMinutes(this.reportRange[1], range)]),
          ]
          break
        case '1T':
          range = differenceInMinutes(this.reportRange[1], this.reportRange[0])
          this.reportRange = [
            addMinutes(this.reportRange[0], -range * 2),
            min([today, addMinutes(this.reportRange[1], range * 2)]),
          ]
          break
      }
    },
    onReportZoomIn(data) {
      if (!data) {
        return
      }

      switch (this.granularity) {
        case '1Y':
          this.reportRange = [addYears(parseISO(data.x), -1), addYears(parseISO(data.x), 1)]
          break
        case '1M':
          this.reportRange = [addMonths(parseISO(data.x), -1), addMonths(parseISO(data.x), 1)]
          break
        case '7D':
          this.reportRange = [addWeeks(parseISO(data.x), -1), addWeeks(parseISO(data.x), 1)]
          break
        case '1D':
          this.reportRange = [addDays(parseISO(data.x), -1), addDays(parseISO(data.x), 1)]
          break
        case '1H':
          this.reportRange = [addHours(parseISO(data.x), -3), addHours(parseISO(data.x), 3)]
          break
        case '15T':
          this.reportRange = [addHours(parseISO(data.x), -1), addHours(parseISO(data.x), 1)]
          break
      }
    },

    setReportRange() {
      const range = differenceInDays(
        Date.parse(this.reportRange[1]),
        Date.parse(this.reportRange[0])
      )

      if (!range) {
        this.reportRange = [addMonths(new Date(), -1), addHours(new Date(), 2)]
      }
    },
  },

  watch: {
    reportRange(n, o) {
      this.getReportingDetails(this.meter)
    },
    chartSettings(n, o) {
      // const newCharts = n.filter(e => e.v)
      // const oldCharts = o.filter(e => e.v)

      // Same charts, different order
      // const areNewChartsContainedInOldCharts = newCharts.every(e => oldCharts.includes(e.c))
      // if (newCharts.length === oldCharts.length && areNewChartsContainedInOldCharts) {
      //   return
      // }

      // Charts changed
      this.getReportingDetails(this.meter)
    },
    meter: {
      immediate: true,
      handler(n) {
        if (this.liveUpdate) {
          this.onToggleLiveUpdate(false)
        }
        this.setReportRange()
        this.getReportingDetails(n)
      },
    },
  },

  props: {
    meter: {
      type: Object,
      required: true,
    },
    volumeReport: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  components: {
    Chart,
    ChartSettings,
    BaseCheck,
    BaseLoader,
    BaseButton,
    BaseIcon,
    BaseButtonIcon,
    BaseDropdown2,
    BaseSelect,
    'a-popover': Popover,
  },
}
</script>

<style scoped></style>
